import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import {GET_RETURN_EXCHANGE_CUSTOMER_POLICY_SETTINGS} from 'gql/queries';
import { SAVE_RETURN_EXCHANGE_CUSTOMER_POLICY_SETTINGS } from 'gql/mutations';
import { NotificationTypes } from 'utils';
import { Link } from 'react-router-dom';
import ReturnsSettingsTabs from './ReturnsSettingsTabs';
import ExchangeCatalogSelector from './ExchangeCatalogSelector';
import DifferentProductExchangeSettings from './DifferentProductExchangeSettings';
import {getRequestApprovalSettings} from 'types/policy';
import {TrashIcon} from '@heroicons/react/outline';
import {getReturnReasonSummary} from './PolicySettings.reasonSummary';
import {
  canonicalExchangePolicy,
  differentProductForEditing,
  DifferentProductPolicy,
  ExchangePolicy,
  ExchangeSettingsRecord,
  EXCHANGE_REASONS,
  exchangePolicyKey,
  exchangeSettingsValidation,
  parseExchangeSettings,
} from './ExchangeSettings.policy';

const clone = (policy: ExchangePolicy): ExchangePolicy =>
  JSON.parse(JSON.stringify(policy));
const toggle = (values: string[], id: string) =>
  values.includes(id)
    ? values.filter((value) => value !== id)
    : [...values, id];
const modes = ['HIDDEN', 'OPTIONAL', 'REQUIRED', 'REASON_BASED'];
const modeLabels: Record<string, string> = {
  HIDDEN: 'Hidden',
  OPTIONAL: 'Optional',
  REQUIRED: 'Required',
  REASON_BASED: 'Based on reason',
};
const reasonGroups = [
  {title: 'Product issues', description: 'Use these when the item, condition, or contents are the problem.', ids: ['WRONG_ITEM', 'DAMAGED', 'NOT_AS_DESCRIBED', 'MISSING_ITEM_OR_PARTS']},
  {title: 'Fit and preference', description: 'Use these when the customer no longer wants to keep the item.', ids: ['DOES_NOT_FIT', 'CHANGED_MIND']},
  {title: 'Delivery and catch-all', description: 'Keep a broad option available for unusual cases or timing issues.', ids: ['ARRIVED_LATE', 'OTHER']},
];
const reasonDescriptions: Record<string, string> = {
  WRONG_ITEM: 'Customer received an item that does not match the order.',
  DAMAGED: 'Item arrived damaged, broken, or not working as expected.',
  NOT_AS_DESCRIBED: 'Item looks, feels, or performs differently than the product page suggested.',
  MISSING_ITEM_OR_PARTS: 'The order is incomplete or pieces are missing from the box.',
  DOES_NOT_FIT: 'Size, fit, or measurements are not right for the customer.',
  CHANGED_MIND: 'Customer no longer wants the item after delivery.',
  ARRIVED_LATE: 'The order arrived too late to be useful for the customer.',
  OTHER: 'Catch-all reason for exchanges that do not fit the main options.',
};
const ExchangeSettings = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const { storeId: selectedStoreId, storeProperties } = useStore();
    const storeId = selectedStoreId || '';
    const [source, setSource] = useState<ExchangeSettingsRecord | null>(null),
      [draft, setDraft] = useState<ExchangePolicy | null>(null);
    const [saving, setSaving] = useState(false),
      [error, setError] = useState<string | null>(null),
      [saved, setSaved] = useState(false),
      [label, setLabel] = useState('');
    const [expandedReasons, setExpandedReasons] = useState<Record<string, boolean>>({});
    const activeStore = useRef(storeId),
      sequence = useRef(0),
      pending = useRef(false);
    const mounted = useRef(true), scope = useRef({ storeId });
    if (scope.current.storeId !== storeId) scope.current = { storeId };
    const ownerScope = scope.current;
    const handlerContext = { ownerScope, source, draft };
    const liveContext = useRef(handlerContext);
    liveContext.current = handlerContext;
    const isLiveHandler = () => mounted.current && scope.current === ownerScope && liveContext.current === handlerContext;
    activeStore.current = storeId;
    useEffect(() => () => { mounted.current = false; sequence.current++; }, []);
    const read = useQuery(GET_RETURN_EXCHANGE_CUSTOMER_POLICY_SETTINGS, {
      variables: { input: { storeId } },
      skip: !storeId,
    });
    const [write] = useMutation(SAVE_RETURN_EXCHANGE_CUSTOMER_POLICY_SETTINGS);
    const raw = read.data?.returnExchangeCustomerPolicySettings;
    const current = useMemo(
      () => parseExchangeSettings(raw, storeId),
      [raw, storeId],
    );
    const dirty =
      !!source &&
      !!draft &&
      exchangePolicyKey(draft) !== exchangePolicyKey(source.policy);
    useEffect(() => {
      sequence.current++;
      pending.current = false;
      setSource(null);
      setDraft(null);
      setSaving(false);
      setError(null);
      setSaved(false);
      setLabel('');
      setExpandedReasons({});
    }, [storeId]);
    useEffect(() => {
      if (!current) return;
      if (
        !source ||
        source.storeId !== storeId ||
        (!dirty && !pending.current && current.revision > source.revision)
      ) {
        setSource(current);
        setDraft(clone(current.policy));
        setSaved(false);
      }
    }, [current, source, storeId, dirty]);
    const bound = source?.storeId === storeId && draft;
    const different = differentProductForEditing(draft);
    const requestApproval = getRequestApprovalSettings(storeProperties?.id === storeId ? storeProperties.policySettings : undefined);
    const enabledReasons = draft ? [
      ...EXCHANGE_REASONS.map(([id, label]) => ({ id, label })),
      ...draft.reasons.customReasons,
    ].filter(reason => [...draft.reasons.enabledPresetReasons, ...draft.reasons.enabledCustomReasonIds].includes(reason.id)) : [];
    const groupedReasons: Array<{title: string; description: string; custom: boolean; reasons: Array<{id: string; label: string; custom: boolean}>}> = [
      ...reasonGroups.map(group => ({...group, custom: false, reasons: EXCHANGE_REASONS.filter(([id]) => group.ids.includes(id)).map(([id, label]) => ({id, label, custom: false}))})),
      {title: 'Custom reasons', description: 'Add store-specific reasons. New reasons start enabled.', custom: true,
        reasons: (draft?.reasons.customReasons || []).map(reason => ({...reason, custom: true}))},
    ];
    const reasonSummary = getReturnReasonSummary([...EXCHANGE_REASONS.map(([id]) => id), ...(draft?.reasons.customReasons || []).map(reason => reason.id)], enabledReasons.map(reason => reason.id));
    function change(next: ExchangePolicy) {
      if (isLiveHandler() && !pending.current && bound) {
        const enabledReasonIds = [...next.reasons.enabledPresetReasons, ...next.reasons.enabledCustomReasonIds];
        setDraft(next.differentProduct ? { ...next, differentProduct: {
          ...next.differentProduct,
          ...('reasonScope' in next.differentProduct ? {
            reasonScope: {...next.differentProduct.reasonScope, reasonIds: next.differentProduct.reasonScope.reasonIds.filter(id => enabledReasonIds.includes(id))},
          } : {reasonIds: next.differentProduct.reasonIds.filter(id => enabledReasonIds.includes(id))}),
        } } : next);
        setError(null);
        setSaved(false);
      }
    }
    function changeDifferent(next: DifferentProductPolicy) {
      if (draft) change({...draft, version: 2, differentProduct: next});
    }
    function changeOriginalScope(mode: 'ALL_POLICY_ELIGIBLE' | 'SELECTED') {
      if (!draft) return;
      change({...draft, version: 2, eligibleOriginals: {
        mode,
        productIds: mode === 'ALL_POLICY_ELIGIBLE' ? [] : draft.eligibleOriginals.productIds,
        collectionIds: mode === 'ALL_POLICY_ELIGIBLE' ? [] : draft.eligibleOriginals.collectionIds,
      }, ...(draft.differentProduct ? {differentProduct: differentProductForEditing(draft)} : {})});
    }
    async function save(): Promise<boolean> {
      if (!isLiveHandler() || pending.current || !bound || !source || !draft) return false;
      const validation = exchangeSettingsValidation(draft);
      if (validation) {
        setError(validation);
        return false;
      }
      const policy = canonicalExchangePolicy(clone(draft)),
        owner = storeId,
        revision = source.revision,
        request = ++sequence.current;
      pending.current = true;
      const isCurrentRequest = () => mounted.current && scope.current === ownerScope && activeStore.current === owner && sequence.current === request;
      setSaving(true);
      setError(null);
      setSaved(false);
      try {
        const response = await write({
          variables: {
            input: { storeId: owner, expectedRevision: revision, policy },
          },
        });
        if (!isCurrentRequest())
          return false;
        const record = parseExchangeSettings(
          response.data?.saveReturnExchangeCustomerPolicySettings,
          owner,
        );
        if (
          !record ||
          !record.configured ||
          record.revision !== revision + 1 ||
          exchangePolicyKey(record.policy) !== exchangePolicyKey(policy)
        )
          throw new Error('EXCHANGE_ACK_UNVERIFIED');
        setSource(record);
        setDraft(clone(record.policy));
        setSaved(true);
        showNotification({
          type: NotificationTypes.success,
          message: 'Exchange settings saved.',
        });
        return true;
      } catch (cause: any) {
        if (isCurrentRequest())
          setError(
            String(cause?.message).includes('EXCHANGE_CUSTOMER_POLICY_CONFLICT')
              ? 'Exchange settings changed in another session. Reload the saved settings before trying again.'
              : 'The save could not be verified. Your edits are still here. Reload the saved settings before trying again.',
          );
        return false;
      } finally {
        if (isCurrentRequest()) {
          pending.current = false;
          setSaving(false);
        }
      }
    }
    async function reload() {
      if (!isLiveHandler() || !bound || !source || pending.current) return;
      const owner = storeId,
        request = ++sequence.current;
      pending.current = true;
      const isCurrentRequest = () => mounted.current && scope.current === ownerScope && activeStore.current === owner && sequence.current === request;
      setSaving(true);
      try {
        const response = await read.refetch({ input: { storeId: owner } });
        if (!isCurrentRequest())
          return;
        const record = parseExchangeSettings(
          response.data?.returnExchangeCustomerPolicySettings,
          owner,
        );
        if (!record || record.revision < source.revision || (record.revision === source.revision &&
          (record.configured !== source.configured || exchangePolicyKey(record.policy) !== exchangePolicyKey(source.policy)))) throw new Error('EXCHANGE_READ_UNVERIFIED');
        setSource(record);
        setDraft(clone(record.policy));
        setError(null);
        setSaved(false);
      } catch {
        if (isCurrentRequest())
          setError(
            'Saved Exchange settings are unavailable. Your edits are still here.',
          );
      } finally {
        if (isCurrentRequest()) {
          pending.current = false;
          setSaving(false);
        }
      }
    }
    function addReason() {
      if (
        !isLiveHandler() || !draft ||
        pending.current ||
        draft.reasons.customReasons.length >= 100 ||
        !label.trim() ||
        label.trim().length > 120 ||
        /[\p{Cc}\p{Cf}]/u.test(label)
      )
        return;
      const bytes = new Uint32Array(4);
      window.crypto.getRandomValues(bytes);
      const id =
        'CUSTOM_R' +
        Array.from(bytes, (value) => value.toString(16).padStart(8, '0'))
          .join('')
          .toUpperCase();
      change({
        ...draft,
        reasons: {
          ...draft.reasons,
          customReasons: [
            ...draft.reasons.customReasons,
            { id, label: label.trim(), analyticsKey: id.toLowerCase() },
          ],
          enabledCustomReasonIds: [...draft.reasons.enabledCustomReasonIds, id],
        },
      });
      setLabel('');
    }
    return (
      <SettingsViewWrapper activeNav="resolution-portal/returns/exchanges" sectionId="returns-settings">
        <div className="min-h-full bg-[#f6f6f7]">
        <div className="max-w-6xl mx-auto space-y-5 px-6 py-6 pb-10">
          <ReturnsSettingsTabs activeTab="exchanges" />
          <div className="sticky top-0 z-20 -mx-2 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-[#f6f6f7]/95 px-2 py-3 backdrop-blur-sm">
            <div><h1 className="text-2xl font-semibold tracking-tight text-gray-900">Exchange settings</h1><p className="mt-1 text-sm text-gray-500">Set eligible items, replacement choices and the details customers provide.</p></div>
            {bound && <div className="flex items-center gap-3">
              {dirty && <span className="text-xs font-medium text-amber-700">Unsaved changes</span>}
              {saved && <span role="status" className="text-xs font-medium text-green-700">Exchange settings saved.</span>}
              <Button isLoading={saving} disabled={!dirty || saving || source!.revision >= 2147483647} onClick={save}>Save Exchange settings</Button>
            </div>}
          </div>
          {!bound ? (
            <p
              role={
                read.error || (!read.loading && !current) ? 'alert' : 'status'
              }
            >
              {read.loading
                ? 'Loading Exchange settings…'
                : 'Exchange settings are unavailable.'}
            </p>
          ) : (
            <>
              <UnsavedChangesGuard hasUnsavedChanges={dirty} onSave={save} />
              {error && (
                <div
                  className="rounded-lg border border-red-200 bg-red-50 p-3"
                  role="alert"
                >
                  {error}
                  <Button variant="outlined" disabled={saving} onClick={reload}>
                    Reload saved Exchange settings
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-4">
                <div><p className="text-sm font-semibold text-gray-900">Offer exchanges in the claim portal</p><p className="mt-1 text-xs leading-5 text-gray-500">Customers see Exchange first and can choose Return instead.</p></div>
                  <Toggle
                    isEnabled={draft!.enabled}
                    disabled={saving}
                    onChange={(enabled) => change({ ...draft!, enabled })}
                    ariaLabel="Enable customer Exchanges"
                    leftLabel="Enable customer Exchanges"
                  />
              </div>
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_17rem] xl:items-start">
              <fieldset disabled={saving} className="min-w-0 space-y-5">
                <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <div className="flex gap-3"><span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">1</span><div><h2 className="text-base font-semibold text-gray-900">Eligible items</h2><p className="mt-1 text-sm text-gray-500">Choose which ordered items customers can exchange.</p></div></div>
                  <fieldset className="space-y-3" aria-label="Exchange item eligibility">
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="exchange-original-scope" checked={draft!.eligibleOriginals.mode === 'ALL_POLICY_ELIGIBLE'} onChange={() => changeOriginalScope('ALL_POLICY_ELIGIBLE')} />All products allowed by Policy</label>
                    <label className="flex items-center gap-2 text-sm"><input type="radio" name="exchange-original-scope" checked={draft!.eligibleOriginals.mode !== 'ALL_POLICY_ELIGIBLE'} onChange={() => changeOriginalScope('SELECTED')} />Only selected products or collections</label>
                  </fieldset>
                  {draft!.eligibleOriginals.mode === 'ALL_POLICY_ELIGIBLE' ? <p className="text-sm text-gray-500">New products are included automatically when they meet your Policy.</p> : <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <ExchangeCatalogSelector key={`${storeId}:originals`} storeId={storeId} disabled={saving} searchLabel="Search eligible items" productLabel="Eligible original products" collectionLabel="Eligible original collections" selected={draft!.eligibleOriginals} onChange={eligibleOriginals => change({...draft!, eligibleOriginals: {...draft!.eligibleOriginals, ...eligibleOriginals}})} />
                    <p className="text-xs text-gray-500">Only matching products or collections can be exchanged. Choosing neither keeps customer Exchanges unavailable.</p>
                  </div>}
                  <div className="space-y-2 border-t border-gray-100 pt-3">
                    <p className="text-sm text-gray-600">Your Policy exclusions apply to ordered items for both returns and exchanges.</p>
                    <p className="text-xs leading-5 text-gray-500">Manage product-name, tag, collection and SKU exclusions in Policy. Rules can apply to all purchases or a specific purchase period.</p>
                    <Link to="/settings/resolution-portal/returns/policy#eligibility-exclusions-panel" className="inline-block text-sm font-medium text-blue-700 hover:underline">Manage Policy exclusions</Link>
                  </div>
                </section>
                <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                  <div className="flex gap-3"><span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">2</span><div><h2 className="text-base font-semibold text-gray-900">Exchange reasons</h2><p className="mt-1 text-sm text-gray-500">Choose customer-facing exchange reasons. Edit an enabled reason to set evidence or require a team review.</p></div></div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3"><p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-green-700">Enabled reasons</p><p className="mt-1 text-sm font-semibold text-green-950">{reasonSummary.enabledLabel}</p><p className="mt-1 text-xs text-green-800">Shown to customers.</p></div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"><p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Disabled reasons</p><p className="mt-1 text-sm font-semibold text-gray-900">{reasonSummary.disabledLabel}</p><p className="mt-1 text-xs text-gray-500">Hidden from customers.</p></div>
                  </div>
                  <div className="space-y-4">{groupedReasons.map(group => <div key={group.title} role="group" aria-label={group.title}
                    className={group.custom ? 'rounded-lg border border-dashed border-blue-200 bg-blue-50/30 p-4' : 'rounded-lg border border-gray-200 bg-white'}>
                    <div className={group.custom ? 'flex items-start justify-between gap-3' : 'flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3'}>
                      <div><h3 className="text-sm font-semibold text-gray-900">{group.title}</h3><p className="mt-0.5 text-xs text-gray-500">{group.description}</p></div>
                      <span className={group.custom ? 'shrink-0 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700' : 'shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600'}>{group.custom ? `${group.reasons.length} custom` : `${group.reasons.filter(reason => enabledReasons.some(enabled => enabled.id === reason.id)).length}/${group.reasons.length} enabled`}</span>
                    </div>
                    {group.custom && <div className="mt-4 flex flex-col gap-3 md:flex-row">
                      <label className="sr-only" htmlFor="new-custom-exchange-reason">Custom Exchange reason</label>
                      <input id="new-custom-exchange-reason" aria-label="Custom Exchange reason" value={label} maxLength={120}
                        onChange={event => setLabel(event.target.value)} onKeyDown={event => {if (event.key === 'Enter') {event.preventDefault();addReason();}}}
                        placeholder='Add a custom reason, like "Ordered multiple sizes"'
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"/>
                      <Button onClick={addReason} disabled={!label.trim() || draft!.reasons.customReasons.length >= 100} className="whitespace-nowrap border-transparent bg-[#2563EB] text-white hover:bg-[#1d4ed8] focus:ring-blue-500">Add reason</Button>
                    </div>}
                    {group.custom && !group.reasons.length ? <p className="mt-4 rounded-lg border border-white/80 bg-white/80 px-4 py-3 text-sm text-gray-500">No custom reasons yet.</p> : <div className={group.custom ? 'mt-4 divide-y divide-gray-100 rounded-lg border border-blue-100 bg-white' : 'divide-y divide-gray-100'}>{group.reasons.map((reason) => {
                    const key = reason.custom
                      ? 'enabledCustomReasonIds'
                      : 'enabledPresetReasons';
                    const enabled = draft!.reasons[key].includes(reason.id);
                    const defect =
                      /DAMAGE|DEFECT|BROKEN/.test(reason.id) ||
                      draft!.reasons.customReasons.some(
                        (value) =>
                          value.id === reason.id &&
                          /DAMAGE|DEFECT|BROKEN/i.test(value.analyticsKey),
                      );
                    const notesMode = defect ? 'REQUIRED' : draft!.evidenceRequirements.byReason[reason.id]?.notes || draft!.evidenceRequirements.defaults.notes;
                    const photosMode = defect ? 'REQUIRED' : draft!.evidenceRequirements.byReason[reason.id]?.photos || draft!.evidenceRequirements.defaults.photos;
                    const manualReview = defect || draft!.manualReviewReasonIds.includes(reason.id);
                    return (
                      <div
                        key={reason.id}
                        className="px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">{reason.custom
                          ? <input aria-label={`Label for custom Exchange reason ${reason.id}`} maxLength={120} value={reason.label} className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-gray-900" onChange={event => change({...draft!, reasons: {...draft!.reasons, customReasons: draft!.reasons.customReasons.map(item => item.id === reason.id ? {...item, label: event.target.value} : item)}})}/>
                          : <p className={`text-sm font-medium ${enabled ? 'text-gray-950' : 'text-gray-500'}`}>{reason.label}</p>}
                        <p className="mt-0.5 text-xs text-gray-500">{reason.custom ? draft!.reasons.customReasons.find(item => item.id === reason.id)?.analyticsKey : reasonDescriptions[reason.id]}</p>
                        {enabled && <p className="mt-1 text-xs leading-5 text-gray-500">Notes {modeLabels[notesMode].toLowerCase()} · Photos {modeLabels[photosMode].toLowerCase()} · {manualReview ? 'Merchant review required' : 'Uses approval settings'}</p>}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                        {enabled && <button type="button" aria-label={`Edit ${reason.label} requirements`} aria-expanded={expandedReasons[reason.id] === true} onClick={() => {if (isLiveHandler() && !pending.current) setExpandedReasons(previous => ({...previous, [reason.id]: !previous[reason.id]}));}} className="rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">{expandedReasons[reason.id] ? 'Done' : 'Edit'}</button>}
                        <Toggle isSmall ariaLabel={reason.label} isEnabled={enabled} disabled={saving} onChange={() =>
                              change({
                                ...draft!,
                                reasons: {
                                  ...draft!.reasons,
                                  [key]: toggle(draft!.reasons[key], reason.id),
                                },
                              })
                            }/>
                        {reason.custom && <button type="button" aria-label={`Remove custom Exchange reason ${reason.id}`} className="inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-400 hover:border-red-200 hover:text-red-500" onClick={() => {
                          const byReason = {...draft!.evidenceRequirements.byReason};delete byReason[reason.id];
                          change({...draft!, reasons: {...draft!.reasons, customReasons: draft!.reasons.customReasons.filter(item => item.id !== reason.id), enabledCustomReasonIds: draft!.reasons.enabledCustomReasonIds.filter(id => id !== reason.id)}, manualReviewReasonIds: draft!.manualReviewReasonIds.filter(id => id !== reason.id), evidenceRequirements: {...draft!.evidenceRequirements, byReason}});
                        }}><TrashIcon className="h-4 w-4"/></button>}
                        </div>
                        </div>
                        {enabled && expandedReasons[reason.id] && (
                          <div className="mt-3 grid gap-3 rounded-lg bg-gray-50 p-3 text-sm sm:grid-cols-2">
                            {(['notes', 'photos'] as const).map((field) => (
                              <label key={field}>
                                {reason.label}: {field}
                                <select className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                  aria-label={`${reason.label}: ${field}`}
                                  disabled={defect}
                                  value={
                                    defect
                                      ? 'REQUIRED'
                                      : draft!.evidenceRequirements.byReason[
                                          reason.id
                                        ]?.[field] || ''
                                  }
                                  onChange={(event) => {
                                    const rule = {
                                      ...draft!.evidenceRequirements.byReason[
                                        reason.id
                                      ],
                                    };
                                    if (event.target.value)
                                      rule[field] = event.target.value as any;
                                    else delete rule[field];
                                    const byReason = {
                                      ...draft!.evidenceRequirements.byReason,
                                      [reason.id]: rule,
                                    };
                                    if (!Object.keys(rule).length)
                                      delete byReason[reason.id];
                                    change({
                                      ...draft!,
                                      evidenceRequirements: {
                                        ...draft!.evidenceRequirements,
                                        byReason,
                                      },
                                    });
                                  }}
                                >
                                  <option value="">Use default</option>
                                  {modes.slice(0, 3).map((mode) => (
                                    <option key={mode} value={mode}>
                                      {modeLabels[mode]}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            ))}
                            <label className="flex items-center gap-2 sm:col-span-2">
                              <input
                                type="checkbox"
                                disabled={defect}
                                checked={
                                  defect ||
                                  draft!.manualReviewReasonIds.includes(
                                    reason.id,
                                  )
                                }
                                onChange={() =>
                                  change({
                                    ...draft!,
                                    manualReviewReasonIds: toggle(
                                      draft!.manualReviewReasonIds,
                                      reason.id,
                                    ),
                                  })
                                }
                              />{' '}
                              Require merchant review
                            </label>
                          </div>
                        )}
                        {enabled && defect && (
                          <p className="mt-1 text-sm text-gray-500">
                            Damage or defect reasons require notes, photos and
                            merchant review.
                          </p>
                        )}
                      </div>
                    );
                  })}</div>}
                  </div>)}</div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h3 className="mb-3 text-sm font-semibold text-gray-900">Evidence defaults</h3>
                    <div className="grid gap-3 sm:grid-cols-2">{(['notes', 'photos'] as const).map(field => <label key={field} className="block text-xs font-semibold text-gray-600">Exchange {field} requirement<select aria-label={`Exchange ${field} requirement`} value={draft!.evidenceRequirements.defaults[field]} onChange={event => change({...draft!, evidenceRequirements: {...draft!.evidenceRequirements, defaults: {...draft!.evidenceRequirements.defaults, [field]: event.target.value}}})} className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900">{modes.map(mode => <option key={mode} value={mode}>{modeLabels[mode]}</option>)}</select></label>)}</div>
                  </div>
                </section>
                <DifferentProductExchangeSettings storeId={storeId} value={different} disabled={saving} reasons={enabledReasons} onChange={changeDifferent}/>
              </fieldset>
              <aside aria-label="Customer experience" className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 xl:sticky xl:top-28">
                <h2 className="text-sm font-semibold text-gray-900">Customer experience</h2>
                <p className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${draft!.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{draft!.enabled ? 'Exchanges enabled' : 'Exchanges disabled'}</p>
                <ol className="space-y-3 text-xs leading-5 text-gray-600"><li><span className="font-semibold text-gray-900">1. Choose items</span><br />{draft!.eligibleOriginals.mode === 'ALL_POLICY_ELIGIBLE' ? 'All products allowed by Policy' : `${draft!.eligibleOriginals.productIds.length} products · ${draft!.eligibleOriginals.collectionIds.length} collections`}</li><li><span className="font-semibold text-gray-900">2. Choose your option</span><br />Another size or color, a different product, or store credit when enabled. Return remains available when eligible.</li><li><span className="font-semibold text-gray-900">3. Choose a reason</span><br />{enabledReasons.length} enabled reasons and their evidence requirements.</li><li><span className="font-semibold text-gray-900">4. Choose a replacement</span><br />Same item, different variant{different.enabled ? ' or an eligible different product.' : '.'}</li><li><span className="font-semibold text-gray-900">5. Review &amp; confirm</span><br />Current prices and availability are checked before confirmation.</li></ol>
                <div className="border-t border-gray-100 pt-3 text-xs leading-5"><p className="font-semibold text-gray-900">Exchange approval</p><p className="mt-1 text-gray-600">{requestApproval ? requestApproval.exchange === 'MANUAL' ? 'Review each exchange request' : 'Automatically approve eligible exchange requests' : 'Approval settings could not be verified'}</p><p className="mt-1 text-gray-500">Review reasons, release conditions and payment checks still apply.</p><Link to="/settings/resolution-portal/returns/approval" className="mt-2 inline-block font-medium text-blue-700 hover:underline">Manage approvals &amp; timing</Link></div>
              </aside>
              </div>
            </>
          )}
        </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);
export default ExchangeSettings;
